// src/components/quality/charts/AssessmentsDynamicsCountByOperatorChart.js
import { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const AssessmentsDynamicsCountByOperatorChart = ({ assessments, fieldOptions }) => {
  const [chartData, setChartData] = useState({});
  const periodType = 'week'
  // Создаем маппинг option_value -> массив оценок
  const getMonday = (date) => {
    const dateCopy = new Date(date);
    const day = dateCopy.getDay();
    const diff = dateCopy.getDate() - day + (day === 0 ? -6 : 1); // Приравниваем воскресенье к прошлой неделе
    return new Date(dateCopy.setDate(diff));
  };

  // Функция для получения последней даты недели (воскресенье)
  const getSunday = (date) => {
    const monday = getMonday(date);
    return new Date(monday.setDate(monday.getDate() + 6));
  };

  // Функция для форматирования даты в строку 'DD.MM.YYYY'
  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Извлекаем все даты из оценок
  const dates = assessments.map(assessment => new Date(assessment.created_at));

  // Определяем диапазон дат
  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));

  // Определяем первую понедельник и последнее воскресенье в диапазоне
  const firstMonday = getMonday(minDate);
  const lastSunday = getSunday(maxDate);

  // Формируем массив периодов на основе periodType
  const periods = [];

  if (periodType === 'week') {
    // Формируем массив недель
    let currentWeekStart = new Date(firstMonday);

    while (currentWeekStart <= lastSunday) {
      const weekStart = new Date(currentWeekStart);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      periods.push({
        start: new Date(weekStart),
        end: new Date(weekEnd),
        label: `${formatDate(weekStart)} - ${formatDate(weekEnd)}`
      });

      // Переходим к следующей неделе
      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
    }
  } else if (periodType === 'month') {
    // Формируем массив месяцев
    const monthsMap = new Map();

    // Извлекаем уникальные месяцы из дат оценок
    assessments.forEach(assessment => {
      const date = new Date(assessment.created_at);
      const year = date.getFullYear();
      const month = date.getMonth(); // 0-11
      const key = `${year}-${month}`;
      const monthName = new Date(year, month, 1).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });

      if (!monthsMap.has(key)) {
        monthsMap.set(key, {
          year,
          month,
          label: monthName
        });
      }
    });

    // Сортируем месяцы по дате (от старых к новым)
    const sortedMonths = Array.from(monthsMap.values()).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });

    // Формируем массив периодов
    periods.push(...sortedMonths);
  }

  // Формируем двумерный массив данных
  const data = {};
  // Инициализируем структуру данных для каждого оператора

  periods.forEach(period => {
    const periodLabel = period.label;
    data[periodLabel] = {};
    fieldOptions.operators.forEach(operator => {
      const operatorFullName = `${operator.surname} ${operator.name}`;
      data[periodLabel][operatorFullName] = [];

    });

  });

  // Распределяем оценки по операторам и периодам
  if (assessments && Array.isArray(assessments)) {
    assessments.forEach(assessment => {
      // console.log('assessment', assessment)
      const assessmentDate = new Date(assessment.created_at);
      // console.log('assessmentDate', assessmentDate)
      // Нормализуем дату оценки (сбрасываем время для корректного сравнения)
      const assessmentDateNormalized = new Date(
        assessmentDate.getFullYear(),
        assessmentDate.getMonth(),
        assessmentDate.getDate()
      );
      
      // Находим подходящий период для оценки
      const period = periods.find(p => {
        // Нормализуем даты периода для сравнения
        const periodStartNormalized = new Date(
          p.start.getFullYear(),
          p.start.getMonth(),
          p.start.getDate()
        );
        const periodEndNormalized = new Date(
          p.end.getFullYear(),
          p.end.getMonth(),
          p.end.getDate()
        );
        
        return assessmentDateNormalized >= periodStartNormalized && 
               assessmentDateNormalized <= periodEndNormalized;
      });
      // Находим оператора для оценки
      const operator = fieldOptions.operators.find(op => op.id === assessment.agent.id);

      if (period && operator) {
        const periodLabel = period.label;
        const operatorFullName = `${operator.surname} ${operator.name}`;
        
        if (data[periodLabel] && data[periodLabel][operatorFullName]) {
          data[periodLabel][operatorFullName].push(assessment);
        }
      }
    });
  }
  console.log('data', data)
  // Сортируем периоды в обратном порядке (от новых к старым)
  const sortedPeriods = [...periods].reverse() <= 6 ? [...periods].reverse() : [...periods].reverse().slice(0, 6);

  // Подготавливаем данные для графика
  useEffect(() => {
    if (!assessments || !fieldOptions || !Array.isArray(assessments) || !Array.isArray(fieldOptions)) return;

    // Создаем массив операторов
    const operators = fieldOptions.operators.map(op => `${op.surname} ${op.name}`);
    
    // Создаем метки для оси X - объединяем операторов по периодам
    const labels = [];
    sortedPeriods.forEach(period => {
      operators.forEach(operator => {
        labels.push(operator);
      });
      labels.push(period.label); // Метка периода после группы операторов
    });
    
    // Создаем данные для каждого оператора
    const datasets = [];
    
    operators.forEach((operator, operatorIndex) => {
      const dataValues = [];
      
      sortedPeriods.forEach(period => {
        const periodLabel = period.label;
        // Добавляем значение для оператора в текущем периоде
        const value = data[periodLabel] && data[periodLabel][operator]
          ? data[periodLabel][operator].length
          : 0;
        dataValues.push(value);
        
        // Добавляем пустое значение после каждого оператора для визуального разделения
        dataValues.push(null);
      });
      
      // Удаляем последнее null значение
      dataValues.pop();
      
      datasets.push({
        label: operator,
        data: dataValues,
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
        borderColor: 'rgba(53, 162, 235, 1)',
        borderWidth: 1,
        borderRadius: 4
      });
    });
    
    // Создаем структуру данных для графика
    const chartData = {
      labels: labels,
      datasets: datasets
    };

    setChartData(chartData);
  }, [assessments, fieldOptions, data]);
  console.log('chartData', chartData)
  const options = {
    indexAxis: 'x',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      title: {
        display: true,
        text: 'Динамика оценок по операторам и периодам'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `Количество: ${context.raw}`;
          }
        }
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        color: '#333',
        font: {
          weight: 'bold',
          size: 12
        },
        formatter: (value) => value,
        offset: 4,
        display: true
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          display: true
        },
        ticks: {
          font: {
            size: 10
          },
          color: '#666',
          maxRotation: 0,
          minRotation: 0,
          callback: function(value, index) {
            // Показываем метку периода каждые N+1 шагов (где N - количество операторов)
            const operatorsCount = fieldOptions.operators.length;
            return (index + 1) % (operatorsCount + 1) === 0 ? this.getLabelForValue(value) : '';
          }
        },
        title: {
          display: true,
          text: 'Операторы и периоды',
          font: {
            size: 14,
            weight: 'bold'
          },
          color: '#666'
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true
        },
        ticks: {
          font: {
            size: 12
          },
          color: '#333',
          stepSize: 1,
          precision: 0
        },
        title: {
          display: true,
          text: 'Количество оценок',
          font: {
            size: 14,
            weight: 'bold'
          },
          color: '#666'
        }
      }
    }
  };
  
  return (
    <div className="chart-card">
      <h3>Динамика оценок по операторам</h3>
      {Object.keys(chartData).length === 0 ? (
        <div className="no-data">Нет данных для отображения</div>
      ) : (
        <div className="chart-container" style={{ padding: '20px', height: '600px', overflow: 'auto' }}>
          <Bar data={chartData} options={options} />
        </div>
      )}
    </div>
  );
};

export default AssessmentsDynamicsCountByOperatorChart;